import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "track",
    "slide",
    "dialog",
    "dialogImage",
    "dialogTitle",
    "dialogPrice",
    "dialogMeta",
    "dialogDescription",
    "dot",
    "status"
  ]

  static values = {
    interval: { type: Number, default: 4200 }
  }

  connect() {
    this.visibleCount = this.getVisibleCount()
    this.logicalIndex = 0
    this.transitioning = false
    this.paused = false
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.originalSlides = Array.from(this.trackTarget.children)
    this.handleResize = this.handleResize.bind(this)
    this.handleTransitionEnd = this.handleTransitionEnd.bind(this)
    this.handlePointerEnter = () => this.pause()
    this.handlePointerLeave = () => this.resume()
    this.handleFocusIn = () => this.pause()
    this.handleFocusOut = (event) => {
      if (!this.element.contains(event.relatedTarget)) this.resume()
    }

    this.trackTarget.addEventListener("transitionend", this.handleTransitionEnd)
    this.element.addEventListener("mouseenter", this.handlePointerEnter)
    this.element.addEventListener("mouseleave", this.handlePointerLeave)
    this.element.addEventListener("focusin", this.handleFocusIn)
    this.element.addEventListener("focusout", this.handleFocusOut)
    window.addEventListener("resize", this.handleResize, { passive: true })

    this.buildLoop()
    this.startAutoplay()
  }

  disconnect() {
    this.stopAutoplay()
    window.removeEventListener("resize", this.handleResize)
    this.trackTarget?.removeEventListener("transitionend", this.handleTransitionEnd)
    this.element.removeEventListener("mouseenter", this.handlePointerEnter)
    this.element.removeEventListener("mouseleave", this.handlePointerLeave)
    this.element.removeEventListener("focusin", this.handleFocusIn)
    this.element.removeEventListener("focusout", this.handleFocusOut)
    this.closeDialog({ animate: false })
  }

  next() {
    this.move(1)
  }

  previous() {
    this.move(-1)
  }

  goTo(event) {
    const index = Number(event.params.index)
    if (!Number.isInteger(index) || index < 0 || index >= this.originalSlides.length) return

    this.logicalIndex = index
    this.currentIndex = this.visibleCount + index
    this.setPosition(true)
    this.updateIndicators()
  }

  move(direction) {
    if (this.transitioning || this.originalSlides.length === 0) return

    this.currentIndex += direction
    this.logicalIndex = this.mod(this.logicalIndex + direction, this.originalSlides.length)
    this.setPosition(true)
    this.updateIndicators()
  }

  open(event) {
    event.preventDefault()

    let record
    try {
      record = JSON.parse(event.currentTarget.dataset.purchaseResultsCarouselRecordParam)
    } catch (_error) {
      return
    }

    if (!this.hasDialogTarget || !record) return

    this.dialogImageTarget.src = record.image
    this.dialogImageTarget.alt = `${record.maker} ${record.model}`
    this.dialogTitleTarget.textContent = `${record.maker} ${record.model}`
    this.dialogPriceTarget.textContent = `買取価格 ${record.price}`
    this.dialogMetaTarget.replaceChildren(
      this.metaItem("年式", record.year),
      this.metaItem("走行距離", record.mileage),
      this.metaItem("車両状態", record.condition),
      this.metaItem("買取地域", record.area)
    )
    this.dialogDescriptionTarget.textContent = record.description
    this.dialogTarget.showModal()
  }

  close(event) {
    event?.preventDefault()
    this.closeDialog()
  }

  closeFromBackdrop(event) {
    if (event.target === event.currentTarget) this.closeDialog()
  }

  afterClose() {
    this.dialogTarget.classList.remove("purchase-results__dialog--closing")
  }

  buildLoop() {
    this.trackTarget.querySelectorAll("[data-carousel-clone]").forEach((slide) => slide.remove())
    this.originalSlides = Array.from(this.trackTarget.children)
    if (this.originalSlides.length === 0) return

    const before = this.originalSlides.slice(-this.visibleCount).map((slide) => this.cloneSlide(slide))
    const after = this.originalSlides.slice(0, this.visibleCount).map((slide) => this.cloneSlide(slide))
    before.reverse().forEach((slide) => this.trackTarget.prepend(slide))
    after.forEach((slide) => this.trackTarget.append(slide))

    this.currentIndex = this.visibleCount + this.logicalIndex
    this.setPosition(false)
    this.updateIndicators()
  }

  cloneSlide(slide) {
    const clone = slide.cloneNode(true)
    clone.dataset.carouselClone = "true"
    return clone
  }

  setPosition(animate) {
    const slide = this.trackTarget.children[this.currentIndex]
    if (!slide) return

    this.trackTarget.style.transition = animate && !this.reduceMotion.matches ? "" : "none"
    this.trackTarget.style.transform = `translate3d(-${slide.offsetLeft}px, 0, 0)`
    this.transitioning = animate && !this.reduceMotion.matches

    if (!this.transitioning) {
      // Ensure the invisible loop correction is painted before transitions are
      // enabled again. Without this, some mobile browsers animate the reset.
      void this.trackTarget.offsetWidth
      requestAnimationFrame(() => { this.trackTarget.style.transition = "" })
    }
  }

  handleTransitionEnd() {
    this.transitioning = false
    const firstRealIndex = this.visibleCount
    const lastRealIndex = this.visibleCount + this.originalSlides.length - 1

    if (this.currentIndex > lastRealIndex) {
      this.currentIndex -= this.originalSlides.length
      this.setPosition(false)
    } else if (this.currentIndex < firstRealIndex) {
      this.currentIndex += this.originalSlides.length
      this.setPosition(false)
    }
  }

  handleResize() {
    const nextVisibleCount = this.getVisibleCount()
    if (nextVisibleCount === this.visibleCount) return

    this.visibleCount = nextVisibleCount
    this.buildLoop()
  }

  updateIndicators() {
    const index = this.mod(this.logicalIndex, this.originalSlides.length || 1)
    this.dotTargets.forEach((dot, dotIndex) => dot.setAttribute("aria-current", String(dotIndex === index)))
    if (this.hasStatusTarget && this.originalSlides[index]) {
      const model = this.originalSlides[index].querySelector(".purchase-results__model")?.textContent?.trim()
      this.statusTarget.textContent = model ? `表示中: ${model}` : ""
    }
  }

  startAutoplay() {
    if (this.reduceMotion.matches || this.originalSlides.length < 2) return
    this.stopAutoplay()
    this.autoplayTimer = window.setInterval(() => {
      if (!this.paused) this.next()
    }, this.intervalValue)
  }

  stopAutoplay() {
    window.clearInterval(this.autoplayTimer)
    this.autoplayTimer = undefined
  }

  pause() {
    this.paused = true
  }

  resume() {
    this.paused = false
  }

  getVisibleCount() {
    return window.matchMedia("(min-width: 768px)").matches ? 4 : 3
  }

  metaItem(label, value) {
    const item = document.createElement("span")
    item.textContent = `${label}: ${value}`
    return item
  }

  closeDialog({ animate = true } = {}) {
    if (!this.hasDialogTarget || !this.dialogTarget.open) return
    this.dialogTarget.close()
  }

  mod(value, length) {
    return ((value % length) + length) % length
  }
}
